/**
 * What is in the graph and what is not, for a reader who will judge the site
 * by its edges before its features. The counts that change with each build
 * come from the build item at render time; the counts of the national Silver
 * are written here with the date they were measured, because they describe
 * the sources rather than the current serving load. They were last measured
 * on the production Silver of 12 October 2026 (part-10 log).
 */
import type { DocPage } from '../types.js';

export const coveragePage: DocPage = {
  slug: 'coverage',
  title: 'What is in the graph',
  audience: 'researchers, grant seekers',
  promise: 'Which organizations have pages, which returns and grants are read, which years and forms, what is left out, and how old the facts are.',
  lead: 'FunderGraph holds what the IRS has released in e-filed form, read through one concordance, plus the master file of exempt organizations. This page says what that covers, what it leaves out, and how far behind the present it runs.',
  sections: [
    {
      id: 'today',
      heading: 'The current build',
      blocks: [
        { kind: 'headline' },
        {
          kind: 'paragraph',
          text: [
            'The date is the date of the build of the facts, not of the last reload of the site; a reload re-projects the same facts. Every page shows the period of each fact beside it, so the date here is a ceiling, not a promise that every organization is that current.',
          ],
        },
      ],
    },
    {
      id: 'organizations',
      heading: 'Organizations',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'An organization has its own page when it files its own return, or when the master file records that it has filed one. In the national Silver built on 12 October 2026, from the master file of October 2026 and the archives through August 2026, 1,537,247 master-file organizations had a return on record and 26,718 filers were not in the master file at all: 1,564,359 pages. A further 427,711 master-file rows had no return on record; those organizations are shown by name and number where a filing names them, without a page of their own.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'The master file carries, for each organization, the IRS subsection, foundation code, ruling date, status, activity code, and the asset, income, and revenue ranges of its last return. The page shows these as words. It does not show a street address, and nothing on the site names a person.',
          ],
        },
      ],
    },
    {
      id: 'returns',
      heading: 'Returns',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The returns are the e-filed Forms 990, 990-EZ, and 990-PF the IRS publishes as XML, read from twenty-five monthly archives through one concordance that maps every schema version the IRS has used to one set of fields. The build of 12 October 2026 read 1,147,944 returns: 585,732 Forms 990, 352,143 Forms 990-EZ, and 210,031 Forms 990-PF, from 128,750 private foundations; every schema version in the archives was mapped. The 990-T returns in the archives are not read, because they carry no grants and no program text. A return the filer amended is kept and marked as replaced, 10,564 of them; totals count the current return for each period.',
          ],
        },
        {
          kind: 'list',
          items: [
            ['From a Form 990: the mission, the program descriptions, Schedule O narratives, and Schedule I, the list of grants to organizations in the United States.'],
            ['From a Form 990-EZ: the program descriptions.'],
            ['From a Form 990-PF: Part XV, the list of grants and contributions paid, which names recipients without their numbers.'],
            ['From every return: the EIN, the name, the city and state, the tax period, and the return type.'],
          ],
        },
      ],
    },
    {
      id: 'grants',
      heading: 'Grants',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A grant edge is one row of a filer’s grant list. The same build held 2,844,195 rows from private foundations’ Part XV and 1,727,800 rows from Schedule I, reported by 157,473 filers. By tax year the rows fall mostly in two years: 2,451,354 for 2024 and 1,594,321 for 2025, with 427,500 for 2023, 91,006 for 2026, and a thin tail before. The site counts a grant once per current return, so an amended return does not double it.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'Of those rows, 1,640,692 Schedule I rows state the recipient’s EIN, and 1,422,383 of them name an organization in the master file. The other 2,914,224 rows name a recipient without a number, every Part XV row among them, 1,121,568 distinct names once case is folded; ',
            { link: '/about/resolution', text: 'how a name becomes an organization' },
            ' says how many of those become links and how the rest are shown.',
          ],
        },
      ],
    },
    {
      id: 'not',
      heading: 'What is not in the graph',
      blocks: [
        {
          kind: 'list',
          items: [
            ['Returns filed on paper. The IRS publishes e-filed returns; most organizations have been required to e-file since tax year 2020, so older paper years are absent and a few filers still are.'],
            ['Form 990-N, the postcard. It carries no financial content and no grants; an organization that files only the postcard has a page from the master file and nothing on it from a return.'],
            ['Returns the IRS has not released. A return is filed five to eleven months after the fiscal year ends and posted later still, so the most recent year is always incomplete.'],
            ['Schedule F, grants outside the United States; Schedule B, the list of contributors; officer and director compensation. The first is out of scope, the other two name people.'],
            ['In the current national build, grant lists read from funders’ websites and federal award records. Both collectors exist and run against the fixture; neither has been run for the country yet. Every grant on the site today comes from a return.'],
            ['Embeddings of organization names for the country, which the nearest-name tier of resolution reads: none has been built, so that tier is inactive nationally and a name that is not an exact or a rule match stays unlinked. Embeddings of missions, programs, and purposes exist for the country since the build of 19 October 2026, 1,991,815 texts; funder discovery, which reads them, is computed for every organization with a mission, 506,000 of them, and is not yet shown on any page.'],
          ],
        },
      ],
    },
    {
      id: 'freshness',
      heading: 'How old the facts are',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'The IRS posts a new archive of returns about once a month. The graph is rebuilt from what has been posted when the build runs, and the build date is on this page and on the home page. Between the end of an organization’s fiscal year and its return appearing here, expect a year or more. A grant reported for tax year 2025 was paid during a year that ended in 2025 and was read in 2026.',
          ],
        },
      ],
    },
  ],
};
